import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutGrid, X, RefreshCw, AlertTriangle, TrendingUp, 
  Globe, Shield, Gauge, ChevronDown, ChevronUp, Loader2, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TheaterComparisonPanel = ({ token, isOpen, onClose }) => {
  const [theaters, setTheaters] = useState([]);
  const [selectedTheaters, setSelectedTheaters] = useState(['hormuz', 'red_sea']);
  const [analyses, setAnalyses] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchTheaters();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && selectedTheaters.length >= 2) {
      fetchAnalyses();
    }
  }, [selectedTheaters, isOpen]);

  const fetchTheaters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/theaters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTheaters(response.data.theaters || []);
    } catch (err) {
      console.error('Error fetching theaters:', err);
    }
  };

  const fetchAnalyses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const results = {};
      
      for (const theaterId of selectedTheaters) {
        try {
          const response = await axios.post(
            `${API_URL}/api/analysis/run?theater_id=${theaterId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          results[theaterId] = response.data.analysis;
        } catch (err) {
          results[theaterId] = { error: err.response?.data?.detail || 'Erreur' };
        }
      }
      
      setAnalyses(results);
    } catch (err) {
      setError('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const toggleTheater = (theaterId) => {
    if (selectedTheaters.includes(theaterId)) {
      if (selectedTheaters.length > 2) {
        setSelectedTheaters(selectedTheaters.filter(id => id !== theaterId));
      }
    } else {
      if (selectedTheaters.length < 4) {
        setSelectedTheaters([...selectedTheaters, theaterId]);
      }
    }
  };

  const exportPDF = async () => {
    if (selectedTheaters.length < 2) {
      toast.error('Sélectionnez au moins 2 théâtres');
      return;
    }
    
    setExporting(true);
    try {
      // First generate the PDF
      const response = await axios.post(
        `${API_URL}/api/theater/comparison/pdf?theater_ids=${selectedTheaters.join(',')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'success') {
        // Download the PDF with authentication
        const pdfUrl = `${API_URL}${response.data.url}`;
        const pdfResponse = await axios.get(pdfUrl, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        // Create blob URL and open in new tab
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Revoke after delay to allow tab to load
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        
        toast.success('Briefing comparatif généré avec succès');
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setExporting(false);
    }
  };

  const getCrisisCodeColor = (code) => {
    switch (code?.toUpperCase()) {
      case 'BLACK': return 'bg-black text-white border-gray-500';
      case 'RED': return 'bg-red-500/20 text-red-400 border-red-500';
      case 'ORANGE': return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'YELLOW': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'BLUE': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      default: return 'bg-green-500/20 text-green-400 border-green-500';
    }
  };

  const getScoreBarWidth = (score) => `${Math.min(100, Math.max(0, score))}%`;
  
  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-red-500';
    if (score >= 60) return 'bg-orange-500';
    if (score >= 40) return 'bg-yellow-500';
    if (score >= 25) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const theaterIcons = {
    'hormuz': '🛢️',
    'polynesia': '🏝️',
    'red_sea': '🚢',
    'south_china_sea': '⚔️',
    'black_sea': '🌑'
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-cyan-500/30 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="theater-comparison-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <LayoutGrid className="w-6 h-6 text-cyan-400" />
              <div>
                <h2 className="font-mono font-bold text-cyan-400 text-lg">COMPARAISON MULTI-THÉÂTRES</h2>
                <p className="text-xs text-slate-400">
                  Sélectionnez 2 à 4 théâtres pour comparer les indicateurs de risque
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={exportPDF}
                disabled={exporting || selectedTheaters.length < 2}
                className="px-3 py-2 bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="export-comparison-pdf-btn"
                title="Exporter briefing comparatif PDF"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                <span className="text-sm font-mono">PDF</span>
              </button>
              <button 
                onClick={fetchAnalyses}
                disabled={loading}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="refresh-comparison-btn"
              >
                <RefreshCw className={`w-5 h-5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Theater selector */}
          <div className="p-4 border-b border-slate-700 bg-slate-800/50">
            <div className="flex flex-wrap gap-2">
              {theaters.map((theater) => (
                <button
                  key={theater.theater_id}
                  onClick={() => toggleTheater(theater.theater_id)}
                  className={`px-3 py-2 rounded-lg border transition-all flex items-center gap-2 ${
                    selectedTheaters.includes(theater.theater_id)
                      ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                      : 'bg-slate-700/30 border-slate-600 text-slate-400 hover:border-cyan-500/50'
                  }`}
                  data-testid={`compare-theater-${theater.theater_id}`}
                >
                  <span>{theaterIcons[theater.theater_id] || '🌍'}</span>
                  <span className="font-mono text-sm">{theater.name_short}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {selectedTheaters.length}/4 théâtres sélectionnés
            </p>
          </div>

          {/* Comparison content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                <span className="ml-3 text-slate-400 font-mono">ANALYSE EN COURS...</span>
              </div>
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
                <AlertTriangle className="w-5 h-5 inline mr-2" />
                {error}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {selectedTheaters.map((theaterId) => {
                  const theater = theaters.find(t => t.theater_id === theaterId);
                  const analysis = analyses[theaterId];
                  const crisis = analysis?.crisis_detection || {};
                  const hasError = analysis?.error;
                  
                  return (
                    <motion.div
                      key={theaterId}
                      className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {/* Theater header */}
                      <div className="p-3 border-b border-slate-700 bg-slate-800">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{theaterIcons[theaterId]}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-white truncate">
                              {theater?.name_short || theaterId}
                            </div>
                            <div className="text-xs text-slate-400 truncate">
                              {theater?.category?.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {hasError ? (
                        <div className="p-4 text-red-400 text-sm">
                          <AlertTriangle className="w-4 h-4 inline mr-1" />
                          {analysis.error}
                        </div>
                      ) : (
                        <div className="p-3 space-y-4">
                          {/* EWS Score */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-slate-400 font-mono">EWS</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getCrisisCodeColor(crisis.crisis_code)}`}>
                                {crisis.crisis_code || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                                <motion.div 
                                  className={`h-full ${getScoreBarColor(crisis.early_warning_score)}`}
                                  initial={{ width: 0 }}
                                  animate={{ width: getScoreBarWidth(crisis.early_warning_score || 0) }}
                                  transition={{ duration: 0.8 }}
                                />
                              </div>
                              <span className="text-white font-bold text-lg w-12 text-right">
                                {crisis.early_warning_score?.toFixed(0) || 0}
                              </span>
                            </div>
                          </div>
                          
                          {/* GTI */}
                          {analysis?.geopolitical_analysis && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-400 font-mono">GTI</span>
                                <span className={`text-sm font-bold ${
                                  analysis.geopolitical_analysis.gti >= 70 ? 'text-red-400' : 
                                  analysis.geopolitical_analysis.gti >= 50 ? 'text-yellow-400' : 'text-green-400'
                                }`}>
                                  {analysis.geopolitical_analysis.gti?.toFixed(0) || 0}
                                </span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${
                                    analysis.geopolitical_analysis.gti >= 70 ? 'bg-red-500' : 
                                    analysis.geopolitical_analysis.gti >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${analysis.geopolitical_analysis.gti || 0}%` }}
                                />
                              </div>
                            </div>
                          )}
                          
                          {/* Risk Assessment */}
                          {analysis?.risk_assessment?.sri && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-400 font-mono">SRI</span>
                                <span className="text-sm font-bold text-cyan-400">
                                  {analysis.risk_assessment.sri?.value?.toFixed(0) || 'N/A'}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Alerts count */}
                          <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                            <span className="text-xs text-slate-400">Alertes</span>
                            <span className={`text-sm font-bold ${
                              analysis?.alerts?.length > 0 ? 'text-red-400' : 'text-green-400'
                            }`}>
                              {analysis?.alerts?.length || 0}
                            </span>
                          </div>
                          
                          {/* Recommendations preview */}
                          {analysis?.recommendations?.length > 0 && (
                            <div className="pt-2 border-t border-slate-700">
                              <div className="text-xs text-slate-400 mb-1">Priorité:</div>
                              <div className="text-xs text-slate-300 line-clamp-2">
                                {analysis.recommendations[0]}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
            
            {/* Summary comparison */}
            {!loading && Object.keys(analyses).length >= 2 && (
              <div className="mt-6 bg-slate-800/50 rounded-lg border border-cyan-500/30 p-4">
                <h3 className="font-mono text-cyan-400 font-bold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  SYNTHÈSE COMPARATIVE
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Highest EWS */}
                  <div className="bg-slate-700/30 rounded p-3">
                    <div className="text-xs text-slate-400 mb-1">EWS le plus élevé</div>
                    <div className="text-white font-bold">
                      {(() => {
                        const highest = Object.entries(analyses)
                          .filter(([_, a]) => a?.crisis_detection)
                          .sort((a, b) => 
                            (b[1].crisis_detection?.early_warning_score || 0) - 
                            (a[1].crisis_detection?.early_warning_score || 0)
                          )[0];
                        if (!highest) return 'N/A';
                        return `${theaterIcons[highest[0]]} ${highest[1].crisis_detection?.early_warning_score?.toFixed(0) || 0}`;
                      })()}
                    </div>
                  </div>
                  
                  {/* Highest GTI */}
                  <div className="bg-slate-700/30 rounded p-3">
                    <div className="text-xs text-slate-400 mb-1">GTI le plus élevé</div>
                    <div className="text-white font-bold">
                      {(() => {
                        const highest = Object.entries(analyses)
                          .filter(([_, a]) => a?.geopolitical_analysis)
                          .sort((a, b) => 
                            (b[1].geopolitical_analysis?.gti || 0) - 
                            (a[1].geopolitical_analysis?.gti || 0)
                          )[0];
                        if (!highest) return 'N/A';
                        return `${theaterIcons[highest[0]]} ${highest[1].geopolitical_analysis?.gti?.toFixed(0) || 0}`;
                      })()}
                    </div>
                  </div>
                  
                  {/* Total alerts */}
                  <div className="bg-slate-700/30 rounded p-3">
                    <div className="text-xs text-slate-400 mb-1">Alertes totales</div>
                    <div className="text-white font-bold">
                      {Object.values(analyses).reduce((sum, a) => sum + (a?.alerts?.length || 0), 0)}
                    </div>
                  </div>
                  
                  {/* Zones at risk */}
                  <div className="bg-slate-700/30 rounded p-3">
                    <div className="text-xs text-slate-400 mb-1">Zones en alerte</div>
                    <div className="text-white font-bold">
                      {Object.values(analyses).filter(a => 
                        (a?.crisis_detection?.early_warning_score || 0) >= 40
                      ).length} / {selectedTheaters.length}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TheaterComparisonPanel;
